import * as React from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Radio from '@mui/material/Radio';
import { useRadioGroup } from '@mui/material/RadioGroup';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AvailableTimeslot, Forecast } from '../../types';
import { tryGetForecastIcon } from '../state/forecastHelper';

type AvailableTimeslotCardProps = {
    time: string;
    date: string;
    forecast?: Forecast;
    numberOfSlots: number;
    selectedDifferentTime: AvailableTimeslot | null;
};

export function AvailableTimeslotCard({
    time,
    date,
    forecast,
    numberOfSlots,
    selectedDifferentTime
}: AvailableTimeslotCardProps) {
    let radioGroup = useRadioGroup();
    let id = `${time}-${date}`;
    let value = `${time};${date}`;
    let isSelected = radioGroup ? radioGroup.value === value : false;

    let availableSlotRef = React.useRef<HTMLLabelElement | null>(null);

    let forecastIcon = forecast ? tryGetForecastIcon(forecast.skyIcon) : null;

    let displayedTime = isSelected && selectedDifferentTime ? selectedDifferentTime.time : time;

    return (
        <label ref={availableSlotRef} htmlFor={id}>
            <Card
                key={id}
                sx={{
                    display: 'flex',
                    padding: '16px',
                    gap: '16px',
                    alignItems: 'center',
                    userSelect: 'none',
                    backgroundColor: isSelected ? '#eef8f4' : 'background.paper',
                    border: isSelected ? '1px solid secondary.dark' : 'none',
                    color: 'text.primary'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '14px',
                            fontWeight: '700',
                            fontFamily: 'Gilroy'
                        }}
                    >
                        <time dateTime={displayedTime}>{displayedTime}</time>
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '12px',
                            fontFamily: 'Gilroy',
                            color: 'text.secondary',
                            textTransform: 'capitalize'
                        }}
                    >
                        <time dateTime={date}>{date}</time>
                    </Typography>
                </Box>
                {numberOfSlots > 1 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            maxWidth: '60px'
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: '12px',
                                fontFamily: 'Gilroy'
                            }}
                        >
                            {`+${numberOfSlots - 1} ${Translate(
                                'se360/photographerbooking/customer/calendar/additional-start-times-label'
                            )}`}
                        </Typography>
                    </Box>
                ) : null}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: '1 auto'
                    }}
                >
                    <Box
                        sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '20px',
                            backgroundColor: '#fff',
                            color: 'text.secondary',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                        }}
                    >
                        {forecast && forecastIcon ? (
                            <img alt="weather forecast" src={forecastIcon} width="32" height="32" />
                        ) : (
                            <span>&mdash;</span>
                        )}
                    </Box>
                </Box>
                <Radio
                    id={id}
                    value={value}
                    checkedIcon={<CheckCircleIcon sx={{ color: 'secondary.dark' }} />}
                    onClick={() => {
                        if (availableSlotRef.current) {
                            setTimeout(() => {
                                // Delay a bit so the height offset has time to be calculated
                                availableSlotRef.current.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                });
                            }, 0);
                        }
                    }}
                />
            </Card>
        </label>
    );
}
